import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, bootPage, ApiError } from '../api';
import { Button } from '../components/Button';
import { Select } from '../components/Select';
import { lbl } from '../labels';
import { Field } from '../components/Field';
import { InfoTip } from '../components/InfoTip';
import { Menu } from '../components/Menu';
import { Modal } from '../components/Modal';
import { Table } from '../components/Table';
import { ApiReference } from './ApiReference';
import type { ApiDocs as ApiDocsFull, McpSetup as McpSetupFull } from './ApiReference';
import { Tabs } from '../components/Tabs';
import { showToast } from '../shell/Toast';

/**
 * Settings — the tab shell, plus two of its five panels.
 *
 * Ported here: Access tokens (the list, with revoke) and the API tab (its documentation and the MCP
 * setup block). Not ported yet, and each says so in its own panel rather than rendering an empty one:
 * profile, brokerage connections and feedback.
 *
 * The panels share ONE payload (`/api/v1/settings/:tab`), which always carries the whole page — so the
 * token list is available on every tab, exactly as the EJS page's locals are.
 */
type Conn = {
  id: number;
  broker: string;
  target_list_id: number | null;
  target_list_name?: string | null;
  queryId?: string;
  /** E*TRADE only: whether today's consent is still valid. Absent for IBKR (its Flex token does not expire). */
  etrade?: {
    environment: 'sandbox' | 'live';
    connected: boolean;
    tokenDay: string | null;
    today: string;
    needsReconnect: boolean;
    accountId: string | null;
    accountName: string | null;
    hasKeys: boolean;
  };
  last?: {
    ok: boolean;
    at?: string;
    imported?: number;
    error?: string;
    running?: boolean;
    /** Rows refreshed by the sync, and whether the report carried a Commission column. */
    rowsUpdated?: number;
    feeFieldMissing?: boolean;
  } | null;
};

type FlexLine = {
  id: number;
  ts: string;
  kind: string;
  host: string;
  outcome: string;
  error_code?: string | null;
  duration_ms?: number | null;
  url_redacted?: string | null;
};

type Token = {
  id: number;
  label: string;
  token_prefix: string;
  scope: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
};

type DocEntry = { method?: string; path: string; scope?: string; summary?: string; note?: string;
  params?: { name?: string; param?: string; required?: boolean; meaning?: string; description?: string }[];
  returns?: string };
/**
 * The documentation payload is NOT a uniform map of lists: REST and SESSION_ONLY are arrays of endpoints,
 * while MCP and DEVICE are single descriptive objects. Rendering it as `entries.map(...)` threw on MCP and
 * took the whole tab down (the error boundary caught it), which is why this is typed loosely and checked.
 */
type ApiDocs = Record<string, DocEntry[] | Record<string, unknown>>;

type McpSetup = {
  base: string;
  endpoint: string;
  whoami: string;
  activate: string;
  pairCodeMinutes: number;
  rateLimit: number;
  tools: { name: string; scope: string; title?: string; summary?: string }[];
};

type DeviceRequest = {
  id: number;
  created_at: string;
  method: string;
  route: string;
  query_summary?: string | null;
  status: number;
  ms?: number | null;
  upstream?: number | null;
  bytes?: number | null;
  token_label?: string | null;
  client?: string | null;
  ua?: string | null;
  ip?: string | null;
  response?: string | null;
};

/** A write a token made — views/settings.ejs's Recent activity row (`mcpAudit`). */
type AuditRow = {
  id: number;
  created_at: string;
  tool?: string;
  args_summary?: string;
  outcome?: string;
  source?: string;
  label?: string;
  revoked_at?: string | null;
};

type DeviceSummary = {
  calls?: number;
  p50?: number;
  p95?: number;
  rateLimited?: number;
  bytes?: number;
  upstream?: number;
};


/** EJS's `ago()` (views/settings.ejs), verbatim: just now / 3m / 2h / 4d / 2mo / 1y. */
function agoLabel(ms: number): string {
  const sec = Math.round((Date.now() - ms) / 1000);
  if (sec < 45) return 'just now';
  const m = Math.round(sec / 60);
  if (m < 60) return m + 'm ago';
  const h = Math.round(m / 60);
  if (h < 24) return h + 'h ago';
  const d = Math.round(h / 24);
  if (d < 30) return d + 'd ago';
  const mo = Math.round(d / 30);
  if (mo < 12) return mo + 'mo ago';
  return Math.round(mo / 12) + 'y ago';
}

/**
 * A stored UTC stamp, rendered as views/settings.ejs's own script renders it: the reader's local time, then
 * the relative age in `span.pf-rel`, with the stored stamp in the tooltip. The SPA printed the raw
 * "2026-10-12 10:45:40" here, which reads as a different day after 17:00 local.
 */
function Utc({ at }: { at: string | null | undefined }) {
  if (!at) return <>—</>;
  const iso = at.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return <>{at}</>;
  const abs = d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  // views/settings.ejs's script REWRITES every `time[data-utc]` into `<span>local</span> (<span class="pf-rel">…)`
  // and marks it data-localized, because it re-localizes on the client. The SPA rendered the local string as a bare
  // text node, so the same cell carried one span where the page carries three, and a re-render had nothing to
  // update. Written the way the script leaves it.
  return (
    <time data-utc={at} dateTime={iso} title={`${at} UTC`} data-localized="1">
      <span>{abs}</span> (<span className="pf-rel" data-ts={String(d.getTime())}>{agoLabel(d.getTime())}</span>)
    </time>
  );
}

type SettingsPayload = {
  tab: string;
  tokens?: Token[];
  connections?: Conn[];
  userLists?: { id: number; name: string }[];
  /** The token minted by the POST that landed here, shown once and then forgotten. */
  newToken?: { label: string; token: string } | null;
  deviceRequests?: DeviceRequest[];
  deviceSummary?: DeviceSummary;
  deviceLabels?: { token_id: number; token_label?: string; n: number }[];
  reqFilters?: { hours?: number; token?: string; status?: string };
  keepDays?: number;
  /** What tokens have changed lately — the Recent activity card. Reads are not recorded. */
  mcpAudit?: AuditRow[];
  /** The reader's own feedback, for the Feedback tab's "My feedback" table. */
  feedback?: OwnFeedback[];
  user?: { basis_method?: string; theme?: string; logo_animate?: boolean };
  mcpSetup?: McpSetup;
  apiDocs?: ApiDocs;
};

/** One of the reader's own feedback rows — views/settings.ejs's `feedback` local (a subset of the admin row). */
type OwnFeedback = {
  id: number;
  created_at: string;
  status: string;
  type?: string;
  title: string;
  body?: string | null;
  has_image?: number;
};

const TABS = [
  { key: 'profile', label: 'Profile', href: '/settings/profile' },
  { key: 'brokerage', label: 'Brokerage', href: '/settings/brokerage' },
  { key: 'feedback', label: 'Feedback', href: '/settings/feedback' },
  { key: 'tokens', label: 'Access tokens', href: '/settings/tokens' },
  { key: 'api', label: 'API reference', href: '/settings/api' },
];


/** Profile — basis method, theme, logo animation and the password, each its own form, as on the EJS page. */
function ProfilePanel({ user, onSaved }: { user: { basis_method?: string; theme?: string; logo_animate?: boolean } ; onSaved: () => void }) {
  const [basis, setBasis] = useState(user.basis_method || 'FIFO');
  const [theme, setTheme] = useState(user.theme || 'light');
  const [animate, setAnimate] = useState(!!user.logo_animate);
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwMsg, setPwMsg] = useState<string | null>(null);

  const post = async (url: string, body: unknown) => {
    const r = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => null);
    if (!r.ok || !j?.ok) throw new Error(j?.error || `HTTP ${r.status}`);
    onSaved();
  };

  /**
   * A settings save, as the page does it: the write, then "✓ Saved" in the toast, then the settings payload again.
   *
   * `onSaved` refreshed only the settings payload, so the one thing a save of the LOGO ANIMATION changes — the
   * shell's header — kept its old look, and a save with no toast left the form looking untouched. That is exactly
   * "clicking save doesn't do anything": the write had already landed. The EJS page reloads on `?saved=1`, which is
   * both of these effects in one.
   */
  const save = async (url: string, body: unknown) => {
    try {
      await post(url, body);
      showToast('✓ Saved');
    } catch (e) {
      showToast('Could not save — ' + (e as Error).message);
    }
  };

  return (
    <div id="tab-profile" role="tabpanel">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
        <div className="panel-card" style={{ marginTop: 0 }}>
          <h2 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
            Cost basis method{' '}
            {/* The tip explains a choice that changes every gain figure on the site, so it belongs beside the
                control rather than in a doc a reader has to find. The words are the EJS tip's, not a fresh
                explanation: the two front ends must say the same thing about the same setting. */}
            <InfoTip anchor="inline" place="below-left">
              Method used when selling (default for new sales)
            </InfoTip>
          </h2>
          <form
            method="post"
            action="/settings/basis-method"
            onSubmit={(e) => {
              e.preventDefault();
              void save('/api/v1/settings/basis-method', { basis_method: basis });
            }}
          >
            {/* The app's own dropdown, not a native select: the EJS page has none of those, and the suite (and
                app.css) address `.sel-btn` inside the form. */}
            <Select
              id="basis_method"
              name="basis_method"
              value={basis}
              onChange={setBasis}
              ariaLabel="Cost basis method"
              options={['FIFO', 'LIFO', 'HIFO', 'SPECIFIC'].map((m) => ({
                value: m,
                label: m === 'SPECIFIC' ? 'Specific lots (choose at sale)' : m,
              }))}
            />
            <p>
              <button type="submit">Save</button>
            </p>
          </form>
        </div>

        <div className="panel-card" style={{ marginTop: 0 }}>
          <h2 style={{ margin: '0 0 12px' }}>Color theme</h2>
          <form
            method="post"
            action="/settings/theme"
            onSubmit={(e) => {
              e.preventDefault();
              // The theme is applied immediately as well as saved: the point is to SEE it, not to reload into it.
              document.documentElement.dataset.theme = theme;
              void save('/api/v1/settings/theme', { theme });
            }}
          >
            <Select
              id="theme"
              name="theme"
              value={theme}
              onChange={setTheme}
              ariaLabel="Color theme"
              options={[
                ['light', 'Light'],
                ['dark', 'Dark'],
                ['midnight', 'Midnight'],
                ['sepia', 'Sepia'],
                ['bitcoin', 'Bitcoin'],
              ].map(([v, text]) => ({ value: v, label: text }))}
            />
            <p>
              <button type="submit">Save</button>
            </p>
          </form>
        </div>

        <div className="panel-card" style={{ marginTop: 0 }}>
          <h2 style={{ margin: '0 0 12px' }}>Logo animation</h2>
          <form
            method="post"
            action="/settings/logo-animate"
            onSubmit={(e) => {
              e.preventDefault();
              void save('/api/v1/settings/logo-animate', { logo_animate: animate });
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" name="logo_animate" value="1" checked={animate} onChange={(e) => setAnimate(e.target.checked)} style={{ width: 'auto' }} />
              Animate the logo's eyes
            </label>
            <p>
              <button type="submit">Save</button>
            </p>
          </form>
        </div>

        <div className="panel-card" style={{ marginTop: 0 }}>
          <h2 style={{ margin: '0 0 12px' }}>Change password</h2>
          <form
            method="post"
            action="/settings/password"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await post('/api/v1/settings/password', { current_password: curPw, new_password: newPw });
                setCurPw('');
                setNewPw('');
                setPwMsg('Password changed.');
              } catch (err) {
                setPwMsg((err as Error).message);
              }
            }}
          >
            <Field label="Current password" htmlFor="cur_pw">
              <input id="cur_pw" name="current_password" type="password" autoComplete="current-password" value={curPw} onChange={(e) => setCurPw(e.target.value)} required />
            </Field>
            <Field label="New password (min 8 characters)" htmlFor="new_pw" style={{ marginTop: 10 }}>
              <input id="new_pw" name="new_password" type="password" autoComplete="new-password" minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)} required />
            </Field>
            {/* views/settings.ejs writes a plain <button> here — the app's base button, not the .btn
                component (which is 2px taller and carries its own padding). */}
            <p>
              <button type="submit">Update password</button>
            </p>
            {pwMsg ? <p className="muted t-small">{pwMsg}</p> : null}
          </form>
        </div>
      </div>
    </div>
  );
}

function NotPorted({ name, detail }: { name: string; detail: string }) {
  return (
    <section className="panel-card">
      <h2 style={{ marginTop: 0 }}>{name}</h2>
      <p className="muted t-body">{detail}</p>
    </section>
  );
}

function TokensPanel({
  tokens,
  newToken,
  onRevoke,
  onRename,
  busy,
  onCreated,
  deviceRequests,
  deviceLabels,
  summary,
  keepDays,
  setup,
  docs,
  audit,
}: {
  tokens: Token[];
  /** The MCP endpoint/tool registry and the API reference's copy of the pairing flow (views/settings.ejs
      receives both on this tab, so the two disclosures here are the reference's own words). */
  setup?: McpSetupFull;
  docs?: ApiDocsFull;
  audit?: AuditRow[];
  newToken?: { label: string; token: string } | null;
  onRevoke: (id: number) => void;
  onRename: (id: number, label: string) => void;
  busy: boolean;
  onCreated: () => void;
  deviceRequests: DeviceRequest[];
  /** The token filter's choices and the request-log summary line (views/settings.ejs's own locals). */
  deviceLabels?: { token_id: number; token_label?: string; n: number }[];
  summary?: DeviceSummary;
  keepDays?: number;
}) {
  // Live tokens by default, revoked ones behind a toggle — the same choice the EJS page makes (it shows
  // six live rows and a "Show N revoked" link, not eighteen rows including dead ones).
  // Live tokens by default, exactly as views/settings.ejs does: the heading says how many are live, the revoked
  // ones are behind "Show N revoked" (or ?revoked=1). Showing all of them by default made the card 576px taller
  // than the EJS card and read as a table of eighteen rows where the page promises a list of live keys.
  const [showRevoked, setShowRevoked] = useState(() => new URLSearchParams(window.location.search).get('revoked') === '1');
  const [newLabel, setNewLabel] = useState('');
  const [newScope, setNewScope] = useState('read');
  const [created, setCreated] = useState<{ label: string; token: string } | null>(null);
  // The revoke asks first, in the app's one confirm dialog, whose OK names what it will do.
  const [revoking, setRevoking] = useState<Token | null>(null);
  const [openDr, setOpenDr] = useState<string | null>(null);
  // The inline rename: one row at a time, as the EJS page's pencil does.
  const [renaming, setRenaming] = useState<number | null>(null);
  const rateLimit = setup?.rateLimit;
  const [renameValue, setRenameValue] = useState('');
  // One clock for the whole table. It ticks on the app's own 30s timer and whenever the tab comes back —
  // a page left open for an hour should not still say "just now".
  // The live tail: off until asked for, and then a 3s poll that prepends what it has not seen. It is off by
  // default for the same reason the EJS page's is — a settings tab left open should not poll forever.
  // The device-request filters, read from the URL the GET form submits to.
  const search = new URLSearchParams(window.location.search);
  const hours = Number(search.get('hours')) || 24;
  const tokenFilter = search.get('token') || '';
  const statusFilter = search.get('status') || '';
  // A filter change is a navigation with that one parameter replaced — the EJS form's `this.form.submit()`.
  const navigateWith = (patch: Record<string, string>) => {
    const q = new URLSearchParams(window.location.search);
    Object.entries(patch).forEach(([k, v]) => (v ? q.set(k, v) : q.delete(k)));
    window.location.href = '/settings/tokens' + (q.toString() ? '?' + q.toString() : '');
  };
  const [live, setLive] = useState(false);
  const [liveRows, setLiveRows] = useState<DeviceRequest[]>([]);
  const allRequests = [...liveRows, ...deviceRequests].filter(
    (r, i, arr) => arr.findIndex((x) => x.id === r.id) === i,
  );

  // The relative ages keep counting (views/settings.ejs sets a 30s interval and a visibilitychange listener,
  // both calling `tick`): the absolute stamp stays put and only `span.pf-rel` is rewritten, so a page left open
  // on a second monitor does not say "just now" an hour later. A hidden tab skips ticks and catches up the
  // moment it is visible again.
  useEffect(() => {
    const tick = () => {
      document.querySelectorAll<HTMLElement>('span.pf-rel[data-ts]').forEach((span) => {
        const next = agoLabel(Number(span.dataset.ts));
        if (span.textContent !== next) span.textContent = next;
      });
    };
    const id = window.setInterval(() => {
      if (!document.hidden) tick();
    }, 30000);
    const onVisible = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // The device-request live tail (views/settings.ejs's `#dr-live`): opt-in, off by default, and it only ever
  // reads this user's own rows. The first poll fires on the click itself — a tail that waits 3s to show you
  // anything looks broken — and each poll asks for rows newer than the newest id on screen, so nothing is
  // duplicated and nothing is skipped. A failed poll is not worth a banner; the next one retries.
  const rowsRef = useRef<DeviceRequest[]>([]);
  rowsRef.current = allRequests;
  useEffect(() => {
    if (!live) return;
    let stopped = false;
    const poll = async () => {
      try {
        const ids = rowsRef.current.map((r) => r.id);
        const after = ids.length ? Math.max(...ids) : 0;
        const res = await fetch(`/settings/tokens/requests/tail.json?after=${after}`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        const body = (await res.json()) as { rows?: DeviceRequest[] };
        const rows = body.rows || [];
        if (stopped || !rows.length) return;
        setLiveRows((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          const fresh = rows.filter((r) => !seen.has(r.id));
          // The endpoint answers oldest-first; the table reads newest-first, so the batch is flipped.
          return [...fresh.reverse(), ...prev];
        });
      } catch {
        /* the next poll retries */
      }
    };
    void poll();
    const id = window.setInterval(() => void poll(), 3000);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [live]);
  const createToken = useMutation({
    mutationFn: async (body: { label: string; scope: string }) => {
      const r = await fetch('/api/v1/tokens', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok || !j?.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      return j.data as { label: string; token: string };
    },
    onSuccess: (d) => {
      setCreated({ label: d.label, token: d.token });
      setNewLabel('');
      onCreated();
    },
  });
  // The client setup's endpoint, from the instance the reader is actually using.
  const endpoint = `${window.location.origin}/api/mcp`;
  const revoked = tokens.filter((t) => t.revoked_at);
  const liveTokens = tokens.filter((t) => !t.revoked_at);
  const shown = showRevoked ? tokens : tokens.filter((t) => !t.revoked_at);
  return (
    <div id="tab-tokens" role="tabpanel">
      {/* views/settings.ejs renders this tab as four cards: Access tokens (what a token is, how to connect a
          client, how to pair a device, the create form), Existing tokens, Recent device requests and Recent
          activity. The SPA had put all four in one card, with the form above the paragraph that explains what
          a token is and neither of the two disclosure blocks. */}
      <div className="panel-card" style={{ marginTop: 0 }}>
        <h2 style={{ margin: '0 0 6px' }}>Access tokens</h2>
        <p className="t-body" style={{ color: 'var(--muted)', margin: '0 0 14px' }}>
          A token lets an MCP client or a script act as you without your password. A <strong>read</strong> token
          can only look; a <strong>read-write</strong> token can also change your lists and transactions. Treat a
          token the way you treat your password. Revoking one takes effect on its next call, and tokens never
          expire on their own.
        </p>

        <details className="t-body" id="mcp-setup" style={{ margin: '0 0 16px' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--accent)', fontWeight: 600 }}>
            How to connect a client
          </summary>
          <div style={{ marginTop: 10, display: 'grid', gap: 12 }}>
            <p className="t-body" style={{ color: 'var(--muted)', margin: 0 }}>
              Point the client at this endpoint and give it a token as a bearer credential. It is{' '}
              <strong>stateless</strong>: every request carries the token, and nothing is remembered between calls.
            </p>
            <div style={{ display: 'grid', gap: 6 }}>
              <label htmlFor="mcp-endpoint">Endpoint (Streamable HTTP, POST)</label>
              <div className="copy-row">
                <input
                  className="t-small"
                  id="mcp-endpoint"
                  readOnly
                  value={`${endpoint}`}
                  onClick={(e) => e.currentTarget.select()}
                  style={{ fontFamily: 'monospace' }}
                />
                <button type="button" className="ghost js-copy" onClick={() => void navigator.clipboard?.writeText(endpoint)}>
                  Copy
                </button>
              </div>
              <label htmlFor="mcp-header">Authorization header</label>
              <div className="copy-row">
                <input
                  className="t-small"
                  id="mcp-header"
                  readOnly
                  value="Authorization: Bearer <your-token>"
                  onClick={(e) => e.currentTarget.select()}
                  style={{ fontFamily: 'monospace' }}
                />
                <button type="button" className="ghost js-copy" onClick={() => void navigator.clipboard?.writeText('Authorization: Bearer <your-token>')}>
                  Copy
                </button>
              </div>
            </div>
            <div>
              <p style={{ margin: '0 0 6px', color: 'var(--muted)' }}>
                In a client's MCP config (the file's location differs per client):
              </p>
              {/* The config block is a copy row like the rest: views/settings.ejs pairs the pre with its own
                  Copy button, and without it the pre was the only block a reader had to select by hand. */}
              <div className="copy-row">
                <pre className="t-small" id="mcp-config" style={{ margin: 0, padding: 10, overflowX: 'auto', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 8 }}>
{`{
  "mcpServers": {
    "portfoolio": {
      "url": "${endpoint}",
      "headers": { "Authorization": "Bearer <your-token>" }
    }
  }
}`}
                </pre>
                <button
                  type="button"
                  className="ghost js-copy"
                  onClick={() =>
                    void navigator.clipboard?.writeText(
                      `{\n  "mcpServers": {\n    "portfoolio": {\n      "url": "${endpoint}",\n      "headers": { "Authorization": "Bearer <your-token>" }\n    }\n  }\n}`,
                    )
                  }
                >
                  Copy
                </button>
              </div>
            </div>
            <div>
              <p style={{ margin: '0 0 6px', color: 'var(--muted)' }}>
                Check it before wiring anything — this reports the account and scope the token carries:
              </p>
              <div className="copy-row">
                <pre
                  className="t-small"
                  id="mcp-check"
                  style={{ margin: 0, padding: 10, overflowX: 'auto', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 8 }}
                >
{`curl -H "Authorization: Bearer <your-token>" ${endpoint.replace('/api/mcp', '')}/api/whoami`}
                </pre>
                <button
                  type="button"
                  className="ghost js-copy"
                  onClick={() =>
                    void navigator.clipboard?.writeText(
                      `curl -H "Authorization: Bearer <your-token>" ${endpoint.replace('/api/mcp', '')}/api/whoami`,
                    )
                  }
                >
                  Copy
                </button>
              </div>
            </div>
            <p style={{ margin: 0, color: 'var(--muted)' }}>
              Rate limited to <strong>{rateLimit || 0}</strong> calls a minute per token; every write is recorded
              in Recent activity below.
            </p>
            <p style={{ margin: 0, color: 'var(--muted)' }}>
              {(setup?.tools || []).filter((t) => t.scope !== 'read-write').length} tools are callable with any
              token and {(setup?.tools || []).filter((t) => t.scope === 'read-write').length} more with a
              read-write token. The full list, with what each one does, is on the{' '}
              <a href="/settings/api">API reference</a> tab.
            </p>
          </div>
        </details>

        {/* Pairing is started by the DEVICE, so this cannot be a button: it explains the shape of the flow and
            where the code gets typed, and its summary is the API reference's own text rather than a second copy
            of it, so the two cannot drift apart. */}
        {setup && docs ? (
          <details className="t-body" id="device-pairing" style={{ margin: '0 0 16px' }}>
            <summary style={{ cursor: 'pointer', color: 'var(--accent)', fontWeight: 600 }}>
              Pairing a device that cannot type
            </summary>
            <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
              <p style={{ margin: 0, color: 'var(--muted)' }}>{docs.DEVICE.summary}</p>
              <ol style={{ margin: 0, paddingLeft: 20, color: 'var(--muted)', display: 'grid', gap: 4 }}>
                <li>
                  The device asks for a code and shows it on its screen, e.g. <code>8WSM-QC88</code>.
                </li>
                <li>
                  Open <code>{setup.activate}</code> in a browser you are signed in to, enter the code, and name
                  the device however you like.
                </li>
                <li>
                  It collects a read-only token within seconds and appears in the list below, where you can rename
                  or revoke it.
                </li>
              </ol>
              <p style={{ margin: 0, color: 'var(--muted)' }}>
                Codes last {setup.pairCodeMinutes} minutes and work once. Re-pairing creates a second token rather
                than replacing the first, so revoke the old one if you are replacing it.
              </p>
            </div>
          </details>
        ) : null}

        {created || newToken ? (
          <div className="panel-card" style={{ margin: '0 0 14px' }}>
            <p className="muted t-small" style={{ marginTop: 0 }}>
              Copy it now — this is the only time {(created || newToken)!.label} is shown.
            </p>
            <div className="copy-row">
              <input className="t-small" id="new-token-value" readOnly value={(created || newToken)!.token} onClick={(e) => e.currentTarget.select()} style={{ fontFamily: 'monospace' }} />
              <Button className="js-copy" onClick={() => void navigator.clipboard?.writeText((created || newToken)!.token)}>Copy token</Button>
            </div>
          </div>
        ) : null}

        <form
          method="post"
          action="/settings/tokens"
          style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}
          // Native submit: the POST redirects back to this tab, and the token arrives in the page's payload
          // shown once. A fetch here would have to re-invent that one-shot (and the "only time it is shown"
          // promise is the point of it).

        >
          <Field label="Label" htmlFor="token_label" required style={{ flex: '1 1 220px' }}>
            <input
              id="token_label"
              name="label"
              required
              maxLength={60}
              placeholder="e.g. Claude Desktop"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
            />
          </Field>
          <Field label="Scope" htmlFor="token_scope">
            <Select
              id="token_scope"
              name="scope"
              value={newScope}
              onChange={setNewScope}
              ariaLabel="Scope"
              options={[
                { value: 'read', label: 'Read only' },
                { value: 'read-write', label: 'Read & write' },
              ]}
            />
          </Field>
          {/* A bare submit, as views/settings.ejs writes it: no .btn class, no size — the page's own button
              stylesheet supplies the box. */}
          <p style={{ margin: '0 0 1px' }}>
            <button type="submit" disabled={createToken.isPending || !newLabel.trim()}>
              {createToken.isPending ? 'Creating…' : 'Create token'}
            </button>
          </p>
        </form>
      </div>

      <div className="panel-card">
        <h2 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          Existing tokens
          <span className="t-body" style={{ color: 'var(--muted)', fontWeight: 400 }}>
            {liveTokens.length} live{revoked.length ? ` · ${revoked.length} revoked` : ''}
          </span>
          {revoked.length ? (
            <a
              className="t-body"
              href={showRevoked ? '/settings/tokens' : '/settings/tokens?revoked=1'}
              style={{ marginLeft: 'auto' }}
              onClick={(e) => {
                e.preventDefault();
                setShowRevoked((v) => !v);
              }}
            >
              {showRevoked ? 'Hide' : 'Show'} {revoked.length} revoked
            </a>
          ) : null}
        </h2>
        <Table
          expandable={false}
          tableId="tokens-table"
          tableClass=""
          columns={[
            { label: 'Label' },
            { label: 'Token' },
            { label: 'Scope' },
            { label: 'Created' },
            { label: 'Last used' },
            { label: 'Status' },
            { label: 'Actions' },
          ]}
          rows={shown.map((t) => ({
            key: t.id,
            cells: [
              // The label cell is editable in place, as views/settings.ejs builds it: the name, the pencil and
              // the rename form are SIBLINGS in the cell (no wrapper element), and the form is in the DOM at
              // rest — hidden — exactly as the page's script toggles it.
              <>
                <span className="tok-label" id={`tok-label-${t.id}`} style={renaming === t.id ? { display: 'none' } : undefined}>
                  {t.label}
                </span>{' '}
                <button
                  type="button"
                  className="icon-btn js-label-edit"
                  title="Rename"
                  aria-label="Rename"
                  style={{
                    width: 19,
                    height: 19,
                    minHeight: 0,
                    minWidth: 0,
                    color: 'var(--muted)',
                    ...(renaming === t.id ? { display: 'none' } : null),
                  }}
                  onClick={() => {
                    setRenaming(t.id);
                    setRenameValue(t.label);
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                </button>
                <form
                    method="post"
                    action={`/settings/tokens/${t.id}/label`}
                    id={`tok-label-form-${t.id}`}
                    className="tok-label-form"
                    style={{ display: renaming === t.id ? 'inline-flex' : 'none', gap: 6, alignItems: 'center' }}
                    onSubmit={(e) => {
                      e.preventDefault();
                      onRename(t.id, renameValue);
                      setRenaming(null);
                    }}
                  >
                    <input
                      autoFocus
                      name="label"
                      maxLength={60}
                      // At rest the field carries the current label, as the page renders it: the form is in the DOM
                      // hidden, so a reader (or a no-JS post) sees the name it would overwrite, not an empty box.
                      value={renaming === t.id ? renameValue : t.label}
                      onChange={(e) => setRenameValue(e.target.value)}
                      style={{ width: 190, margin: 0 }}
                      data-1p-ignore=""
                      data-lpignore="true"
                      data-form-fill-ignore=""
                    />
                    <button type="submit" className="ghost" style={{ padding: '3px 8px' }}>
                      Save
                    </button>
                    <button type="button" className="ghost js-label-cancel" data-token={t.id} style={{ padding: '3px 8px' }} onClick={() => setRenaming(null)}>
                      Cancel
                    </button>
                  </form>
              </>,
              { content: `${t.token_prefix}…`, className: 't-small', attrs: { style: 'font-family: monospace; color: var(--muted)' } },
              // The EJS table says the scope in words, as a chip, and colours read-write: "read" and
              // "read-write" are the wire values, not what a reader is shown.
              t.scope === 'read-write' ? (
                <span key="sc" className="tag neg" title="can change your lists and transactions">
                  Read &amp; write
                </span>
              ) : (
                <span key="sc" className="tag">
                  Read only
                </span>
              ),
              <Utc key="cr" at={t.created_at} />,
              <Utc key="lu" at={t.last_used_at} />,
              t.revoked_at ? (
                <span key="s" className="tag" title={`revoked ${t.revoked_at} UTC`}>
                  Revoked {String(t.revoked_at).slice(0, 10)}
                </span>
              ) : (
                <span key="s" className="tag pos">
                  Live
                </span>
              ),
              // views/settings.ejs's actions cell: the token's activity link, then a revoke FORM (the page posts
              // it and confirms through the shared modal), on one line because the cell is white-space: nowrap.
              {
                attrs: { style: 'white-space: nowrap;' },
                content: (
                  <>
                    <a className="t-small" href={`/settings/tokens?token=${t.id}#activity`} title="What this token has done">
                      activity
                    </a>
                    {t.revoked_at ? null : (
                      <form
                        method="post"
                        action={`/settings/tokens/${t.id}/revoke`}
                        data-confirm={`Revoke “${t.label}”? Anything using it stops working on its next call, and this cannot be undone.`}
                        data-confirm-ok="Revoke"
                        style={{ margin: '0 0 0 8px', display: 'inline-flex', verticalAlign: 'middle' }}
                        onSubmit={(e) => {
                          e.preventDefault();
                          setRevoking(t);
                        }}
                      >
                        <button
                          type="submit"
                          className="icon-btn"
                          title="Revoke"
                          aria-label="Revoke"
                          disabled={busy}
                          style={{ width: 19, height: 19, minHeight: 0, minWidth: 0, color: 'var(--red)' }}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" />
                          </svg>
                        </button>
                      </form>
                    )}
                  </>
                ),
              },
            ],
          }))}
          empty="No tokens yet."
        />
        <Modal
          id="confirm-modal"
          open={!!revoking}
          onClose={() => setRevoking(null)}
          title={revoking ? `Revoke “${revoking.label}”?` : 'Revoke this token?'}
          subtitle="A revoked token stops working on its next call. This cannot be undone."
          tone="danger"
          size="sm"
        >
          <div className="modal-actions" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setRevoking(null)}>
              Cancel
            </Button>
            <Button
              id="confirm-modal-ok"
              variant="danger"
              disabled={busy}
              onClick={() => revoking && onRevoke(revoking.id)}
            >
              Revoke
            </Button>
          </div>
        </Modal>
      </div>

      <div className="panel-card" id="device-requests">
        <h2 style={{ margin: '0 0 10px', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          Recent device requests
          <a
            className="t-body"
            href={`/settings/tokens/requests.csv?token=${new URLSearchParams(window.location.search).get('token') || ''}&hours=${new URLSearchParams(window.location.search).get('hours') || 24}`}
            style={{ marginLeft: 'auto', fontWeight: 400 }}
          >
            Download CSV
          </a>
          <button
            type="button"
            id="dr-live"
            className="ghost t-small"
            style={{ minHeight: 26, padding: '3px 10px' }}
            aria-pressed={live}
            onClick={() => {
              setLive((v) => {
                if (v) setLiveRows([]);
                return !v;
              });
            }}
          >
            Live tail: {live ? 'on' : 'off'}
          </button>
        </h2>
        <p className="t-body" style={{ margin: '0 0 10px', color: 'var(--muted)' }}>
          Every call made with one of your tokens — the watch, a widget, a script — with what it asked for, how
          long it took and how much it returned. Kept for {keepDays || 7} days. Reads are recorded here; data
          CHANGES are in Recent activity below.
        </p>

        {/* The window/token/status filters are a GET form in views/settings.ejs: the page reloads with the
            parameters, and the server narrows the rows. Same here, so a filtered view is a URL you can keep. */}
        <form
          method="get"
          action="/settings/tokens"
          style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end', margin: '0 0 12px' }}
        >
          <label className="t-small" style={{ display: 'grid', gap: 3 }}>
            Window
            <Select
              name="hours"
              value={String(hours)}
              ariaLabel="Window"
              onChange={(v) => navigateWith({ hours: v })}
              options={[1, 6, 24, 168].map((h) => ({ value: String(h), label: h === 168 ? '7 days' : `${h}h` }))}
            />
          </label>
          <label className="t-small" style={{ display: 'grid', gap: 3 }}>
            Token
            <Select
              name="token"
              value={tokenFilter}
              ariaLabel="Token"
              onChange={(v) => navigateWith({ token: v })}
              options={[
                { value: '', label: 'all' },
                ...(deviceLabels || []).map((l) => ({
                  value: String(l.token_id),
                  label: `${l.token_label || `token ${l.token_id}`} (${l.n})`,
                })),
              ]}
            />
          </label>
          <label className="t-small" style={{ display: 'grid', gap: 3 }}>
            Status
            <Select
              name="status"
              value={statusFilter}
              ariaLabel="Status"
              onChange={(v) => navigateWith({ status: v })}
              options={[
                { value: '', label: 'any' },
                ...[200, 400, 401, 404, 429, 500].map((st) => ({ value: String(st), label: String(st) })),
              ]}
            />
          </label>
          <span className="t-small" style={{ color: 'var(--muted)', marginLeft: 'auto' }}>
            {(summary?.calls as number) || 0} calls
            {summary?.p50 != null ? ` · p50 ${summary.p50} ms · p95 ${summary.p95} ms` : ''}
            {summary?.rateLimited ? ` · ${summary.rateLimited} rate-limited` : ''}
            {summary?.bytes ? ` · ${summary.bytes >= 1024 ? (summary.bytes / 1024).toFixed(1) + ' KB' : summary.bytes + ' B'}` : ''}
            {summary?.upstream ? ` · ${summary.upstream} upstream` : ''}
          </span>
        </form>
        {/* views/settings.ejs wraps this table in a `.table-scroll` of its own, on top of the one the component
            renders — so the page's DOM has two nested scroll boxes here and the parity count sees one more. */}
        <div className="table-scroll">
        <Table
          expandable={false}
          bodyId="dr-body"
          tableClass="holdings-table"
          // The page's own widths (views/settings.ejs's dr table): eight fixed columns and the query taking the
          // remainder. Without them the auto layout gave the query 823px and squeezed When to 57px — the same
          // table, 70px out of step with the EJS page in five columns.
          columns={[
            { label: 'When', width: 128 },
            { label: 'Request', width: 160 },
            { label: 'Asked for' },
            { label: 'Status', width: 58, num: true },
            { label: 'ms', width: 56, num: true },
            {
              label: <span title="Upstream (Yahoo) fetches this request caused: 0 means every ticker was served from cache">Up</span>,
              width: 54,
              num: true,
            },
            { label: 'Bytes', width: 68, num: true },
            { label: 'Token', width: 120 },
            { label: 'Client · IP', width: 150 },
          ]}
          // Only a row with a recorded body is clickable, and only one body is open at a time.
          onClickRow={(k) => setOpenDr((cur) => (cur === k ? null : k))}
          rows={allRequests.map((r) => ({
            key: r.id,
            rowClass: 'dr-row' + (r.response ? ' row-action' : ''),
            pane: r.response
              ? {
                  open: openDr === String(r.id),
                  rowClass: 'dr-detail',
                  render: 'lazy',
                  content: (
                    <>
                      {/* The header names the call the body answers: a wall of JSON with no route above it
                          is a body you cannot place. */}
                      <div className="t-small muted" style={{ marginBottom: 6 }}>
                        {r.route} · {r.status}
                      </div>
                      <pre className="t-small" style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {prettyBody(r.response)}
                      </pre>
                    </>
                  ),
                }
              : undefined,
            rowAttrs: { 'data-id': r.id, ...(r.response ? { 'data-response': '1' } : {}) },
            cells: [
              // views/settings.ejs writes a bare `<time data-utc>` here and lets the layout's pass rewrite it into
              // "local (2d ago)" — the same shape <Utc> renders, with the same span.pf-rel.
              <span key="w" className="t-small" style={{ whiteSpace: 'nowrap' }}>
                <Utc at={r.created_at} />
              </span>,
              <span key="r" className="t-small" style={{ fontFamily: 'monospace' }}>
                {r.method} {r.route}
              </span>,
              <div key="q" className="t-small" style={{ overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
                {r.query_summary || '—'}
              </div>,
              <span key="s" className={'t-small' + (r.status >= 400 ? ' neg' : '')}>
                {r.status}
              </span>,
              <span key="m" className="t-small" style={{ whiteSpace: 'nowrap' }}>
                {r.ms == null ? '—' : Math.round(r.ms)}
              </span>,
              <span key="u" className="t-small">{r.upstream || 0}</span>,
              <span key="b" className="t-small" style={{ whiteSpace: 'nowrap' }}>
                {r.bytes == null ? '—' : r.bytes}
              </span>,
              <span key="t" className="t-small" style={{ whiteSpace: 'normal' }}>
                {r.token_label || '—'}
              </span>,
              <span key="c" className="t-small" style={{ color: 'var(--muted)' }}>
                <div style={{ maxWidth: 150, whiteSpace: 'normal' }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.client || (r.ua ? String(r.ua).slice(0, 18) : '—')}
                  </div>
                  <div className="t-tiny" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.8 }}>
                    {r.ip || '—'}
                  </div>
                </div>
              </span>,
            ],
          }))}
          empty="No token traffic in this window."
        />
        </div>
        {/* The table's own vocabulary, in words: "Up" is not a column a first-time reader can guess. */}
        <p className="t-small" style={{ margin: '8px 0 0', color: 'var(--muted)' }}>
          <strong>Up</strong> = upstream (Yahoo) fetches this request caused. <strong>0</strong> means every ticker
          it asked for came from the cache — the cheap case. The client column shows the identifier the app sent
          (<code>X-Portfoolio-Client</code>, else its user agent) above the address it came from.
        </p>
      </div>

      <div className="panel-card" id="activity">
        <h2 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          Recent activity
        </h2>
        <p className="t-body" style={{ margin: '0 0 12px', color: 'var(--muted)' }}>
          What your tokens have done lately. Reads are not recorded — only calls that can change your data,
          including ones that were refused. Arguments are summarised, never stored in full. The client address
          and user agent are recorded too: both are the caller&apos;s own claims about itself, so they tell you
          WHICH client a call came from and never whether to trust it.
        </p>
        {(audit || []).length === 0 ? (
          <p className="t-body" style={{ color: 'var(--muted)', margin: 0 }}>No writes yet.</p>
        ) : (
          <Table
            expandable={false}
            wrap
            tableClass=""
            style={{ minWidth: 1120 }}
            columns={[
              { label: 'When', width: 230 },
              { label: 'Tool', width: 175 },
              { label: 'Arguments' },
              { label: 'Outcome', width: 165 },
              { label: 'Token', width: 150 },
              { label: 'From', width: 150 },
            ]}
            rows={(audit || []).map((a) => ({
              key: a.id,
              cells: [
                // The page writes a bare <time data-utc>; the layout pass rewrites it into "local (2d ago)".
                <span key="w" className="t-small" style={{ whiteSpace: 'nowrap' }}>
                  <Utc at={a.created_at} />
                </span>,
                <span key="t" className="t-small" style={{ fontFamily: 'monospace' }}>{a.tool || '—'}</span>,
                <div key="a" className="t-small" style={{ maxWidth: 300, overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
                  {a.args_summary || '—'}
                </div>,
                <span key="o" className="t-small" style={{ whiteSpace: 'normal' }}>{a.outcome || '—'}</span>,
                <span key="k" className="t-small" style={{ whiteSpace: 'normal' }}>
                  {a.label || '—'}
                  {a.revoked_at ? <span style={{ color: 'var(--muted)' }}> (revoked)</span> : null}
                </span>,
                <div
                  key="f"
                  className="t-small"
                  style={{ color: 'var(--muted)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={a.source || 'recorded before this column existed'}
                >
                  {a.source || '—'}
                </div>,
              ],
            }))}
          />
        )}
      </div>
    </div>
  );
}


/** The Feedback tab — views/settings.ejs's "My feedback": the reader's own rows, each expandable to its body. */
function FeedbackPanel({ rows }: { rows: OwnFeedback[] }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div id="tab-feedback">
      <div className="panel-card" style={{ marginTop: 0 }}>
        <h2 style={{ margin: '0 0 4px' }}>My feedback</h2>
        <p className="muted t-small" style={{ margin: '0 0 12px' }}>
          Bugs and feature requests you&apos;ve sent (via the user menu → Send feedback).
        </p>
        {!rows.length ? (
          <p className="muted t-body">No feedback sent yet.</p>
        ) : (
          <Table
            // The page renders the chevron itself, in the last column, and expands on the row: `chevron: false`
            // keeps the component from adding its own 40px column.
            chevron={false}
            tableClass="holdings-table row-hover"
            columns={[
              { label: lbl('Created') },
              { label: lbl('Status') },
              { label: lbl('Title') },
              { label: '', width: 20 },
            ]}
            onClickRow={(k) => setOpen((cur) => (cur === Number(k) ? null : Number(k)))}
            rows={rows.map((f) => {
              const cls =
                f.status === 'in_progress' || f.status === 'done' ? 'pos' : f.status === 'wontfix' ? 'neg' : '';
              return {
                key: f.id,
                rowClass: 'fb-row row-action',
                rowAttrs: { tabindex: '0', 'aria-label': 'Feedback — expand details', 'data-fb-row': f.id },
                pane: {
                  open: open === f.id,
                  rowClass: 'fb-detail detail-row',
                  rowAttrs: { 'data-detail-for': f.id },
                  content: (
                    <>
                      <pre
                        className="t-body"
                        style={{
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: 'var(--text)',
                          fontFamily: 'inherit',
                        }}
                      >
                        {f.body}
                      </pre>
                      {f.has_image ? (
                        <div style={{ marginTop: 10 }}>
                          <a href={`/feedback/${f.id}/image`} target="_blank" rel="noopener" title="Open full size">
                            <img
                              src={`/feedback/${f.id}/image`}
                              alt="Screenshot attached to this feedback"
                              style={{
                                maxWidth: '100%',
                                maxHeight: 420,
                                border: '1px solid var(--border)',
                                borderRadius: 6,
                                display: 'block',
                              }}
                            />
                          </a>
                          <div className="muted t-small" style={{ marginTop: 4 }}>
                            Screenshot — click to open full size
                          </div>
                        </div>
                      ) : null}
                    </>
                  ),
                },
                cells: [
                  { content: f.created_at.slice(0, 10), className: 'muted' },
                  <span key="s" className={`tag ${cls}`.trim()}>{f.status}</span>,
                  <>
                    {f.has_image ? (
                      <span
                        title="Has a screenshot attached"
                        style={{ display: 'inline-flex', verticalAlign: -2, marginRight: 5, color: 'var(--muted)' }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ display: 'block' }}
                        >
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                        </svg>
                      </span>
                    ) : null}
                    {f.title.length > 80 ? f.title.slice(0, 80) + '…' : f.title}
                  </>,
                  {
                    // The cell carries the page's own padding/positioning; the chevron div inside it is the
                    // only child, exactly as views/settings.ejs writes the cell.
                    attrs: { style: 'padding-left: 14px; position: relative;' },
                    content: (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        right: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 8,
                        paddingRight: 10,
                      }}
                    >
                      <span className="chev" style={{ display: 'inline-flex' }}>
                        <svg width="12" height="12" viewBox="0 0 12 12" style={{ display: 'block' }}>
                          <path
                            d="M2 4 L6 8 L10 4"
                            fill="none"
                            stroke="var(--muted)"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </div>
                    ),
                  },
                ],
              };
            })}
          />
        )}
      </div>
    </div>
  );
}

export function Settings({ tab: initial = 'profile' }: { tab?: string }) {
  const [tab, setTab] = useState(initial);
  const qc = useQueryClient();
  const q = useQuery<SettingsPayload, ApiError>({
    queryKey: ['settings', tab],
    // Served with the shell: the tab has its data on the first paint, and the one-shot token minted by the POST
    // that landed here rides along (the fetch cannot carry it — it is a session value, not a database one).
    initialData: bootPage<SettingsPayload>('settings'),
    // The tokens tab's filters (window, token, status) are the EJS page's GET parameters, so they travel with
    // the request; every other tab ignores the query string.
    queryFn: () => apiGet<SettingsPayload>(`/api/v1/settings/${tab}${window.location.search}`) });

  const rename = useMutation({
    mutationFn: async ({ id, label }: { id: number; label: string }) => {
      const res = await fetch(`/api/v1/tokens/${id}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ label }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });

  const revoke = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/v1/tokens/${id}`, { method: 'DELETE', credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });

  if (q.isLoading) return <main>Loading…</main>;
  if (q.error) return <main className="panel-card">Could not load settings: {q.error.message}</main>;

  const data = q.data!;

  return (
    <main>
      <h1>Settings</h1>
      <Tabs
        // views/partials/settings-tabs.ejs passes `id: 'settings-tabs'`, which the tabs component writes on the
        // bar (and on each tab as `<id>-tab-<key>`). Without it the settings bar was the only tab bar in the app
        // with no id for the page's own CSS or a test to address.
        id="settings-tabs"
        variant="segmented"
        raised
        ariaLabel="Settings"
        active={tab}
        style={{ margin: '0 0 16px' }}
        onChange={setTab}
        items={TABS}
      />

      {tab === 'tokens' ? (
        <TokensPanel
          tokens={data.tokens ?? []}
          newToken={data.newToken ?? null}
          onRevoke={(id) => revoke.mutate(id)}
          onRename={(id, label) => rename.mutate({ id, label })}
          busy={revoke.isPending}
          onCreated={() => qc.invalidateQueries({ queryKey: ['settings'] })}
          deviceRequests={data.deviceRequests ?? []}
          deviceLabels={data.deviceLabels ?? []}
          summary={data.deviceSummary as DeviceSummary | undefined}
          keepDays={data.keepDays}
          setup={data.mcpSetup}
          docs={data.apiDocs as ApiDocsFull | undefined}
          audit={data.mcpAudit ?? []}
        />
      ) : null}
      {tab === 'api' ? <ApiReference setup={data.mcpSetup as McpSetupFull} docs={data.apiDocs as ApiDocsFull} /> : null}
      {tab === 'profile' ? (
        <ProfilePanel
          user={(data.user ?? {}) as { basis_method?: string; theme?: string; logo_animate?: boolean }}
          // The shell reads `logo_animate` and `theme` off whoami, so a profile save must refresh BOTH: the
          // settings payload (the form) and whoami (the header). Refreshing only the first is why saving the logo
          // animation changed the database and nothing on screen.
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['settings'] });
            qc.invalidateQueries({ queryKey: ['whoami'] });
          }}
        />
      ) : null}
      {tab === 'brokerage' ? (
        <BrokeragePanel
          connections={(data.connections ?? []) as Conn[]}
          lists={(data.userLists ?? []) as { id: number; name: string }[]}
          onChanged={() => qc.invalidateQueries({ queryKey: ['settings'] })}
        />
      ) : null}
      {false ? (
        <NotPorted name="Brokerage connections" detail="Not ported yet: the connection list with its add/edit/delete/sync controls — the write endpoints for those do not exist yet either (they are on the task list for this slice)." />
      ) : null}
      {tab === 'feedback' ? <FeedbackPanel rows={data.feedback ?? []} /> : null}
    </main>
  );
}

/** A recorded response, pretty-printed: the body is for reading, not for copying as one line. */
function prettyBody(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

/**
 * Brokerage connections: the list, and the four things a row can do. The menu is one ⋯ per row rather than four
 * standing buttons — they are all secondary, and Remove sat within a misclick of Sync now.
 */
function BrokeragePanel({
  connections,
  lists,
  onChanged,
}: {
  connections: Conn[];
  lists: { id: number; name: string }[];
  onChanged: () => void;
}) {
  const [menu, setMenu] = useState<number | null>(null);
  // The connection menu is drawn as a fixed overlay anchored to its ⋯ button (the shared Menu), because the
  // holdings table that holds it lives in a .table-scroll: an absolutely positioned menu grew that scroll box
  // and moved the panel instead of floating over the row — the same failure the tax page's payment popover had.
  const menuAnchors = useRef<Record<number, { current: HTMLButtonElement | null }>>({});
  const refFor = (id: number) => (menuAnchors.current[id] ??= { current: null });
  const [editing, setEditing] = useState<Conn | null>(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Conn | null>(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState<number | null>(null);
  // The modal's three fields are CONTROLLED, because opening it for a row has to put that row's values in them.
  // The SPA left the list at '' and the inputs uncontrolled, so "Edit" opened an empty form: the saved target list
  // was never selected, the query ID came from a `defaultValue` that only applied at first mount, and Save then
  // wrote blanks over the connection.
  const [listId, setListId] = useState('');
  const [token, setToken] = useState('');
  const [queryId, setQueryId] = useState('');
  const [connError, setConnError] = useState<string | null>(null);
  // The Flex log is a pane under its connection row, and only one is open at a time.
  const [flexOpen, setFlexOpen] = useState<number | null>(null);
  const [flexRows, setFlexRows] = useState<Record<number, FlexLine[]>>({});
  const [flexLoading, setFlexLoading] = useState(false);
  // One line's detail at a time, inside the log: the same "single" rule the log's own rows follow.
  const [flexLine, setFlexLine] = useState<number | null>(null);
  const [lastNote, setLastNote] = useState<Record<number, string>>({});
  // Two credential shapes in one dialog: IBKR's Flex token+query id, or E*TRADE's app key/secret (the
  // per-day OAuth token is not typed by anyone — it arrives on the consent callback).
  const [provider, setProvider] = useState<'ibkr' | 'etrade'>('ibkr');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [etradeEnv, setEtradeEnv] = useState<'sandbox' | 'live'>('sandbox');
  // The consent round trip lands back here with ?etrade=connected (or the reason it failed). Say which, then
  // strip the parameter so a refresh does not repeat a stale message.
  useEffect(() => {
    const flag = new URLSearchParams(window.location.search).get('etrade');
    if (!flag) return;
    const u = new URL(window.location.href);
    u.searchParams.delete('etrade');
    window.history.replaceState({}, '', u.pathname + (u.search || ''));
    // Deferred: this panel's mount effect runs BEFORE the shell's ToastHost subscribes, so an immediate
    // dispatch is heard by nobody (the strip never appears, though the URL was cleaned).
    setTimeout(() => {
      showToast(flag === 'connected' ? '✓ E*TRADE connected — valid until midnight ET' : 'E*TRADE: ' + flag);
    }, 0);
  }, []);
  // A click anywhere else closes the menu, the way every menu on this app closes.
  useEffect(() => {
    if (menu === null) return;
    const close = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest('.conn-actions')) return;
      setMenu(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menu]);

  const open = adding || !!editing;
  const closeModal = () => {
    setAdding(false);
    setEditing(null);
    setMenu(null);
    setConnError(null);
  };

  /** Open the dialog for a row (or for a new one), with every field seeded the way the page's openModal does. */
  const openConn = (c: Conn | null) => {
    setEditing(c);
    setAdding(!c);
    setToken('');
    setQueryId(c?.queryId || '');
    setProvider(c?.broker === 'etrade' ? 'etrade' : 'ibkr');
    // A saved app key/secret never renders back: the fields start empty and say they are kept.
    setConsumerKey('');
    setConsumerSecret('');
    setEtradeEnv(c?.etrade?.environment || 'sandbox');
    // Edit: the connection's saved list. New: the first selectable list, as the page picks the first
    // `option:not([disabled])` (its own placeholder is disabled).
    setListId(c?.target_list_id ? String(c.target_list_id) : (lists[0] ? String(lists[0].id) : ''));
    setConnError(null);
    setMenu(null);
  };

  const save = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const body: Record<string, string | undefined> = {
      broker: provider,
      listId: String(form.get('listId') || ''),
    };
    if (provider === 'ibkr') {
      body.token = String(form.get('token') || '') || undefined;
      body.queryId = String(form.get('queryId') || '');
    } else {
      body.consumerKey = String(form.get('consumerKey') || '') || undefined;
      body.consumerSecret = String(form.get('consumerSecret') || '') || undefined;
      body.environment = etradeEnv;
    }
    if (!body.listId) {
      setConnError('Choose a list to import into');
      return;
    }
    if (provider === 'ibkr' && !editing && !body.token) {
      setConnError('Token is required');
      return;
    }
    if (provider === 'etrade' && !editing && (!body.consumerKey || !body.consumerSecret)) {
      setConnError('Consumer key and secret are required');
      return;
    }
    setBusy(true);
    setConnError(null);
    try {
      const r = await fetch(editing ? `/api/v1/connections/${editing.id}` : '/api/v1/connections', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => null);
      // The page keeps the dialog open and says why; closing on a failed write is how a connection was lost.
      if (!r.ok || !j?.ok) {
        setConnError(j?.error || `Save failed (HTTP ${r.status})`);
        return;
      }
      closeModal();
      showToast('✓ Saved');
      onChanged();
      // A new E*TRADE connection has no token yet: the day's consent is the next step, so take the browser
      // straight to E*TRADE's authorize page instead of leaving a row that can only fail its first sync.
      if (provider === 'etrade' && !editing) {
        const id = (j as { id?: number }).id;
        if (id) window.location.assign(`/connections/${id}/etrade/connect`);
      }
    } finally {
      setBusy(false);
    }
  };

  const doRemove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await fetch(`/api/v1/connections/${removing.id}/delete`, { method: 'POST' });
      setRemoving(null);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const syncNow = async (c: Conn) => {
    setSyncing(c.id);
    // The menu stays open while the run is in flight — the item is where the reader clicked, and it is the
    // place the result appears. It closes when the run does.
    try {
      await fetch(`/connections/${c.id}/sync`, { method: 'POST' });
      // The run is asynchronous on the server, so the row follows it: poll the connection's own last-sync
      // record until it stops running, then say what came in. A row that says "Syncing…" forever is worse
      // than one that says nothing.
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const res = await fetch(`/connections/${c.id}/last`, { headers: { accept: 'application/json' } });
        if (!res.ok) break;
        const j = (await res.json()) as { running?: boolean; added?: number; updated?: number; error?: string; ts?: string };
        if (j.running) continue;
        setLastNote((cur) => ({
          ...cur,
          [c.id]: j.error ? `Failed: ${j.error}` : j.ts ? `${j.ts} — imported ${j.added ?? 0}` : 'Done',
        }));
        break;
      }
      onChanged();
    } finally {
      setSyncing(null);
    }
  };

  /** The Flex log: what IBKR was asked for, one line per request, each line's detail behind it. */
  const toggleFlex = async (c: Conn) => {
    setMenu(null);
    if (flexOpen === c.id) {
      setFlexOpen(null);
      return;
    }
    setFlexOpen(c.id);
    if (flexRows[c.id]) return;
    setFlexLoading(true);
    try {
      const res = await fetch(`/connections/${c.id}/flexlog`, { headers: { accept: 'application/json' } });
      const rows = res.ok ? ((await res.json()) as FlexLine[]) : [];
      setFlexRows((cur) => ({ ...cur, [c.id]: rows }));
    } finally {
      setFlexLoading(false);
    }
  };

  return (
    // views/settings.ejs wraps the tab in #tab-brokerage and the card in a div.panel-card — the tab's id is
    // what the page's own script and CSS address.
    <div id="tab-brokerage">
      <div className="panel-card" style={{ marginTop: 0 }}>
      {/* The EJS tab is ONE card: the heading, the table, its empty line, then the add button right-aligned
          under the table. A second card (or the heading's own primary button) is 14px of card padding and a
          49px offset that every row below inherits. */}
      <h2 style={{ margin: '0 0 12px' }}>Connected brokerages</h2>
      <Table
        expandable={false}
        // No id here: views/settings.ejs's connections table is a bare `table.data-table`, and an invented
        // `#conn-table` made the SPA's table the only one in the app carrying an id nobody addresses. Its classes
        // are explicit too — the component's default adds `row-hover`, which this table does not have.
        tableClass="holdings-table"
        columns={[{ label: 'Brokerage' }, { label: 'Target list' }, { label: 'Last sync' }, { label: '' }]}
        rows={connections.map((c) => ({
          key: c.id,
          rowAttrs: { 'data-conn': c.id },
          pane:
            flexOpen === c.id
              ? {
                  open: true,
                  rowClass: 'conn-flexlog-row',
                  rowAttrs: { 'data-conn': c.id },
                  paneClass: 'conn-flexlog-panel t-small',
                  content: flexLoading ? (
                    <div className="conn-flexlog-loading muted" style={{ padding: '4px 0' }}>
                      Loading…
                    </div>
                  ) : (flexRows[c.id] || []).length ? (
                    <div className="conn-flexlog-mount">
                      <Table
                        header={false}
                        expandable
                        expand="single"
                        tableId={`conn-flexlog-${c.id}`}
                        tableClass="data-table"
                        columns={[{ label: 'Request' }]}
                        onClickRow={(k) => setFlexLine((cur) => (cur === Number(k) ? null : Number(k)))}
                        rows={(flexRows[c.id] || []).map((l) => ({
                          key: l.id,
                          rowClass: 'conn-flexlog-line',
                          cells: [
                            <span key="l" className="t-small">
                              {l.ts} · {l.kind} · {l.host} · {l.outcome}
                            </span>,
                          ],
                          pane: {
                            open: flexLine === l.id,
                            content: (
                              <div className="t-small muted">
                                {l.url_redacted || ''}
                                {l.error_code ? ` · error ${l.error_code}` : ''}
                                {l.duration_ms != null ? ` · ${l.duration_ms}ms` : ''}
                              </div>
                            ),
                          },
                        }))}
                      />
                    </div>
                  ) : (
                    <div className="conn-flexlog-empty muted" style={{ padding: '4px 0' }}>
                      No Flex requests logged yet for this connection.
                    </div>
                  ),
                }
              : undefined,
          cells: [
            <strong key="b">{c.broker === 'etrade' ? 'E*TRADE' : c.broker === 'ibkr' ? 'Interactive Brokers' : c.broker}</strong>,
            c.target_list_name || <span key="l" style={{ color: 'var(--red)' }}>None — choose a list</span>,
            {
              // views/settings.ejs's own cell: `td class="conn-last"` (its script polls cells with
              // [data-running]) holding a span coloured by outcome, with the whole Sync result in it.
              className: 'conn-last',
              attrs: syncing === c.id || c.last?.running ? { 'data-running': '1' } : undefined,
              content:
                syncing === c.id || c.last?.running ? (
                  <span style={{ color: 'var(--muted)' }}>Syncing…</span>
                ) : c.etrade?.needsReconnect ? (
                  // E*TRADE's token is per ET calendar day: a row that still says "imported 12" is stale
                  // until the user re-consents, so the reconnect state leads and the last run follows it.
                  <span style={{ color: 'var(--red)' }}>
                    {c.etrade.connected ? 'Consent expired — Reconnect' : 'Not connected — Reconnect'}
                    {c.last?.ok ? <span className="muted">{` · last: ${c.last.at ?? ''} imported ${c.last.imported ?? 0}`}</span> : null}
                  </span>
                ) : lastNote[c.id] ? (
                  <span style={{ color: c.last?.ok ? 'var(--green)' : 'var(--red)' }}>{lastNote[c.id]}</span>
                ) : c.last ? (
                  <span style={{ color: c.last.ok ? 'var(--green)' : 'var(--red)' }}>
                    {c.last.ok
                      ? `${c.last.at} — imported ${c.last.imported}${c.last.rowsUpdated ? ` · refreshed ${c.last.rowsUpdated} row${c.last.rowsUpdated === 1 ? '' : 's'} (price + fees)` : ''}${c.last.feeFieldMissing ? ' · no commission in the report — tick Commission in the Flex query' : ''}`
                      : `Failed: ${c.last.error}`}
                  </span>
                ) : (
                  <span className="muted">Never</span>
                ),
            },
            {
              // The page right-aligns the cell and pins its width to the button's own line.
              attrs: { style: 'text-align: right; white-space: nowrap;' },
              content: (
            <span className="conn-actions" style={{ position: 'relative', display: 'inline-block' }}>
              <button
                ref={(el) => {
                  refFor(c.id).current = el;
                }}
                type="button"
                className="ghost lot-menu-btn pane-menu-btn"
                aria-label="Connection actions"
                title="Connection actions"
                aria-expanded={menu === c.id}
                style={{ fontWeight: 700 }}
                // Opening is the trigger's job; closing is the outside click's (see the effect below), which is
                // how the EJS menu behaves — a second click on the trigger does not have to mean "close".
                onClick={(e) => {
                  e.stopPropagation();
                  setMenu(c.id);
                }}
              >
                ⋮
              </button>
              <Menu
                id={`conn-menu-${c.id}`}
                open={menu === c.id}
                onClose={() => setMenu(null)}
                anchorRef={refFor(c.id)}
                align="right"
                className="pane-menu"
                minWidth={196}
                shadow="0 8px 30px var(--scrim)"
                portal={false}
                keepMounted
              >
                  <button
                    type="button"
                    role="menuitem"
                    data-menu-item=""
                    className="pane-menu-item conn-sync"
                    data-conn={c.id}
                    disabled={syncing === c.id}
                    onClick={() => void syncNow(c)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path d="M21 12a9 9 0 1 1-3-6.7M21 3v6h-6" />
                    </svg>
                    <span>Sync now</span>
                  </button>
                  {c.broker === 'ibkr' ? (
                  <button
                    type="button"
                    role="menuitem"
                    data-menu-item=""
                    className="pane-menu-item conn-flexlog"
                    data-conn={c.id}
                    onClick={() => void toggleFlex(c)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path d="M4 5h16M4 12h16M4 19h10" />
                    </svg>
                    <span className="conn-flexlog-label">{flexOpen === c.id ? 'Hide log' : 'Flex log'}</span>
                  </button>
                  ) : (
                  <button
                    type="button"
                    role="menuitem"
                    data-menu-item=""
                    className="pane-menu-item conn-reconnect"
                    data-conn={c.id}
                    // A full navigation, not a fetch: the consent page is E*TRADE's own, and the callback
                    // has to come back to this browser's session.
                    onClick={() => window.location.assign(`/connections/${c.id}/etrade/connect`)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8z" />
                    </svg>
                    <span>Reconnect</span>
                  </button>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    data-menu-item=""
                    className="pane-menu-item conn-edit"
                    data-conn={c.id}
                    data-queryid={c.queryId || ''}
                    onClick={() => openConn(c)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                    </svg>
                    <span>Edit</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    data-menu-item=""
                    className="pane-menu-item pane-menu-danger"
                    onClick={() => {
                      setRemoving(c);
                      setMenu(null);
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                    </svg>
                    <span>Remove</span>
                  </button>
              </Menu>
            </span>
              ),
            },
          ],
        }))}
      />
      {/* The EJS page states emptiness UNDER the table and puts the add button after it, right-aligned. */}
      <div
        id="conn-empty"
        className="muted t-body"
        style={{ padding: '6px 0 2px', display: connections.length ? 'none' : 'block' }}
      >
        No brokerages connected yet.
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <button type="button" id="conn-add-btn" className="btn" style={{ padding: '8px 16px' }} onClick={() => openConn(null)}>
          + Connect a brokerage
        </button>
      </div>

      {/* views/partials/modals/conn-body.ejs, ported: the four setup steps with the required Flex fields, then
          the page's three fields in ITS order (token, query ID, list — the dialog asks for the credential first),
          its labels ("Import into list", not "Target list"), the error and status lines, and Cancel/Save. The
          SPA's shorter version was a different dialog with the same id. */}
      <Modal
        id="conn-modal"
        open={open}
        onClose={closeModal}
        title={
          <span id="conn-modal-title">
            {editing
              ? `Edit ${provider === 'etrade' ? 'E*TRADE' : 'Interactive Brokers'} connection`
              : `Connect ${provider === 'etrade' ? 'E*TRADE' : 'Interactive Brokers'}`}
          </span>
        }
        subtitle={
          provider === 'etrade'
            ? 'Imports trades and dividends from E*TRADE via its API (read-only). E*TRADE consent lasts until midnight ET, so the row offers Reconnect each day.'
            : 'Imports trades and dividends from Interactive Brokers via the Flex Web Service (read-only). To set up:'
        }
      >
        {provider === 'ibkr' ? (
        <ol className="t-body" style={{ margin: '0 0 14px', paddingLeft: 20, color: 'var(--text)', display: 'grid', gap: 3 }}>
          <li>In IBKR Client Portal go to Performance &amp; Reports → Flex Queries</li>
          <li>
            Create an Activity Flex Query — include the following Flex query fields (format <strong>XML</strong>):
            <details className="t-small" id="conn-fields-details" open style={{ margin: '8px 0 0' }}>
              <summary style={{ cursor: 'pointer', color: 'var(--muted)', userSelect: 'none' }}>Required Flex query fields</summary>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Trades (Executions)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 10px', color: 'var(--text)' }}>
                    <span>☐ Symbol</span><span>☐ Buy/Sell</span>
                    <span>☐ Quantity</span><span>☐ Trade Price</span>
                    <span>☐ Trade Date</span><span>☐ Currency</span>
                    <span>☐ Commission</span><span>☐ Taxes</span>
                    <span>☐ Asset Category</span>
                    <span>☐ Trade ID</span>
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Cash Transactions</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, color: 'var(--text)' }}>
                    <span>☐ Type</span><span>☐ Amount</span>
                    <span>☐ Description</span><span>☐ Symbol</span>
                    <span>☐ Date</span><span>☐ Currency</span>
                    <span>☐ Transaction ID</span>
                  </div>
                </div>
              </div>
            </details>
          </li>
          <li>Enable Flex Web Service and copy the token</li>
          <li>Paste the token and query ID below, then Sync now</li>
        </ol>
        ) : (
        <ol className="t-body" style={{ margin: '0 0 14px', paddingLeft: 20, color: 'var(--text)', display: 'grid', gap: 3 }}>
          <li>Sign in at E*TRADE → <strong>us.etrade.com/etx/ris/apikey</strong></li>
          <li>Request an <strong>individual</strong> consumer key (sandbox is instant; live needs the one-page API agreement)</li>
          <li>Paste the key and secret below — they are stored on this server and never shown again</li>
          <li>Save, then approve access on the E*TRADE page it opens; you repeat that once a day</li>
        </ol>
        )}

        <form id="conn-form" onSubmit={save}>
          <Field label="Brokerage" htmlFor="conn-provider">
            <Select
              id="conn-provider"
              value={provider}
              onChange={(v) => {
                setProvider(v as 'ibkr' | 'etrade');
                setConnError(null);
              }}
              ariaLabel="Brokerage"
              options={[
                { value: 'ibkr', label: 'Interactive Brokers (Flex)' },
                { value: 'etrade', label: 'E*TRADE' },
              ]}
            />
          </Field>
          {/* The Select is a button, not a native control: the value has to travel with the form. */}
          <input type="hidden" name="provider" value={provider} />
          {provider === 'ibkr' ? (
            <>
              <Field label="Flex token" htmlFor="conn-token">
                <input
                  id="conn-token"
                  name="token"
                  type="password"
                  autoComplete="off"
                  data-1p-ignore=""
                  data-lpignore="true"
                  // The page's own placeholder: on an edit the field is empty and says the saved token is kept.
                  placeholder={editing ? 'Saved — leave blank to keep' : ''}
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                />
              </Field>
              <Field label="Flex query ID" htmlFor="conn-queryid">
                <input
                  id="conn-queryid"
                  name="queryId"
                  type="text"
                  autoComplete="off"
                  data-1p-ignore=""
                  data-lpignore="true"
                  value={queryId}
                  onChange={(e) => setQueryId(e.target.value)}
                />
              </Field>
            </>
          ) : (
            <>
              <Field label="Consumer key" htmlFor="conn-consumer-key">
                <input
                  id="conn-consumer-key"
                  name="consumerKey"
                  type="text"
                  autoComplete="off"
                  data-1p-ignore=""
                  data-lpignore="true"
                  placeholder={editing ? 'Saved — leave blank to keep' : ''}
                  value={consumerKey}
                  onChange={(e) => setConsumerKey(e.target.value)}
                />
              </Field>
              <Field label="Consumer secret" htmlFor="conn-consumer-secret">
                <input
                  id="conn-consumer-secret"
                  name="consumerSecret"
                  type="password"
                  autoComplete="off"
                  data-1p-ignore=""
                  data-lpignore="true"
                  placeholder={editing ? 'Saved — leave blank to keep' : ''}
                  value={consumerSecret}
                  onChange={(e) => setConsumerSecret(e.target.value)}
                />
              </Field>
              <Field label="Environment" htmlFor="conn-etrade-env">
                <Select
                  id="conn-etrade-env"
                  value={etradeEnv}
                  onChange={(v) => setEtradeEnv(v as 'sandbox' | 'live')}
                  ariaLabel="Environment"
                  options={[
                    { value: 'sandbox', label: 'Sandbox (test data)' },
                    { value: 'live', label: 'Live (real account)' },
                  ]}
                />
              </Field>
            </>
          )}
          <Field label="Import into list" htmlFor="conn-list">
            <Select
              id="conn-list"
              value={listId}
              onChange={setListId}
              ariaLabel="Import into list"
              options={lists.map((l) => ({ value: String(l.id), label: l.name }))}
            />
          </Field>
          {/* The Select is a button, not a native control: the value it carries has to travel with the form. */}
          <input type="hidden" name="listId" value={listId} />
          <div className="t-body" id="conn-error" style={{ color: 'var(--red)', marginTop: 10, display: connError ? 'block' : 'none' }}>
            {connError || ''}
          </div>
          <div className="t-body" id="conn-status" style={{ marginTop: 10, display: 'none' }} />
          <div className="modal-actions">
            <button type="button" id="conn-modal-cancel" className="ghost" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit">Save</button>
          </div>
        </form>
      </Modal>

      <Modal
        id="confirm-modal"
        open={!!removing}
        onClose={() => setRemoving(null)}
        title="Remove connection?"
        tone="danger"
      >
        <p id="confirm-modal-msg" className="t-body">
          Remove this brokerage connection? Its import history stays, but nothing will sync again.
        </p>
        <div className="modal-actions" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button id="confirm-modal-cancel" variant="secondary" onClick={() => setRemoving(null)}>
            Cancel
          </Button>
          <Button id="confirm-modal-ok" variant="danger" disabled={busy} onClick={() => void doRemove()}>
            Remove
          </Button>
        </div>
      </Modal>
      </div>
    </div>
  );
}
